import { useQuery } from '@tanstack/react-query'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { FilePlus2, Layers, Settings2, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { loanAccountsQueryOptions } from '@/api/loans-api'
import type { LoanAccountStatus, LoanAccountSummary } from '@/api/loans-types'
import { branchListQueryOptions } from '@/api/reference-api'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { MoneyDisplay } from '@/components/money-display'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { LOAN_ACCOUNT_STATUSES, cycleStatusLabel } from '@/features/loans/constants'
import { LoanLink } from '@/features/loans/loan-link'
import { loanPaths, useLoanNavigate, useRawLoanSearch } from '@/features/loans/loan-paths'
import { useLoanPermissions } from '@/features/loans/permissions'
import { branchLabel } from '@/lib/branch'
import { formatShortDate } from '@/lib/datetime'

import {
  DEFAULT_PAGE_SIZE,
  loanAccountListSearchSchema,
  parseLoanSearch,
  type LoanAccountListSearch,
} from './search'

/**
 * The loan book.
 *
 * A row is an account, not a loan: the account is permanent and its number never changes, and the
 * figures beside it belong to whichever round is currently open on it. An account between rounds
 * has no figures at all, which the list shows as a dash rather than as zero — nothing is
 * outstanding because nothing has been lent, and that is not the same as a round that has been
 * repaid down to nil.
 *
 * Paging and filtering are the server's throughout. A branch's loan book does not fit in a
 * browser, and rows the caller may not see must never be filtered out client-side from a page
 * that already contains them.
 */

const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const helper = dataTableColumns<LoanAccountSummary>()

const columns: DataTableColumn<LoanAccountSummary>[] = helper.columns([
  helper.accessor('accountNumber', {
    header: 'Account no.',
    enableSorting: false,
    cell: (context) => (
      <span className="font-mono text-xs">{context.row.original.accountNumber}</span>
    ),
    meta: { noWrap: true, widthClass: 'w-44', card: 'lead' },
  }),
  helper.accessor('customerName', {
    header: 'Member',
    enableSorting: false,
    cell: (context) => (
      <span className="font-medium text-heading">{context.row.original.customerName}</span>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('productName', {
    header: 'Product',
    enableSorting: false,
    meta: { noWrap: true },
  }),
  helper.accessor('status', {
    header: 'Account',
    enableSorting: false,
    cell: (context) => <StatusBadge status={context.row.original.status} />,
    meta: { widthClass: 'w-28', card: 'lead' },
  }),
  helper.display({
    id: 'round',
    header: 'Current round',
    cell: (context) => {
      const cycle = context.row.original.currentCycle

      if (cycle === null) {
        return (
          <span className="text-xs text-muted">
            No open round · {context.row.original.totalCyclesCompleted} completed
          </span>
        )
      }

      return (
        <span className="flex flex-col gap-0.5">
          <StatusBadge status={cycle.status} />
          <span className="text-2xs text-muted">
            Round {cycle.cycleNumber} · {cycleStatusLabel(cycle.status)}
          </span>
        </span>
      )
    },
    meta: { widthClass: 'w-48' },
  }),
  helper.display({
    id: 'outstanding',
    header: 'Outstanding',
    cell: (context) => {
      const cycle = context.row.original.currentCycle
      return (
        <MoneyDisplay
          value={cycle === null ? null : cycle.totalOutstandingMinor}
          currency={cycle?.currencyCode}
        />
      )
    },
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'Outstanding' },
  }),
  helper.display({
    id: 'arrears',
    header: 'In arrears',
    cell: (context) => {
      const cycle = context.row.original.currentCycle

      if (cycle === null) {
        return <MoneyDisplay value={null} />
      }

      return (
        <span className="flex flex-col items-end gap-0.5">
          <MoneyDisplay value={cycle.amountInArrearsMinor} currency={cycle.currencyCode} />
          {cycle.daysInArrears > 0 ? (
            <span className="text-2xs whitespace-nowrap text-danger-text">
              {cycle.daysInArrears} days
            </span>
          ) : null}
        </span>
      )
    },
    meta: { align: 'right', widthClass: 'w-36', cardLabel: 'In arrears' },
  }),
  helper.accessor('openedDate', {
    header: 'Opened',
    enableSorting: false,
    cell: (context) => formatShortDate(context.row.original.openedDate),
    meta: { noWrap: true, widthClass: 'w-32', card: 'hidden' },
  }),
])

const SEARCH_DEBOUNCE_MS = 300

const ALL = 'all'

export function LoanAccountListPage() {
  const navigate = useLoanNavigate()
  const permissions = useLoanPermissions()
  const search = parseLoanSearch(loanAccountListSearchSchema, useRawLoanSearch())

  const branches = useQuery(branchListQueryOptions())
  const [searchText, setSearchText] = useState(search.search ?? '')

  const page = search.page ?? 1
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE

  const updateSearch = (patch: Partial<LoanAccountListSearch>, replace = false) => {
    navigate(loanPaths.accounts, {
      search: {
        search: search.search,
        status: search.status,
        branchId: search.branchId,
        customerId: search.customerId,
        groupId: search.groupId,
        page: search.page,
        pageSize: search.pageSize,
        ...patch,
      },
      replace,
    })
  }

  // What this screen last wrote into the URL, so a Back button or a pasted link is mirrored into
  // the box without the debounce writing it straight back out.
  const lastPushed = useRef(search.search ?? '')

  useEffect(() => {
    const fromUrl = search.search ?? ''
    if (fromUrl !== lastPushed.current) {
      lastPushed.current = fromUrl
      setSearchText(fromUrl)
    }
  }, [search.search])

  useEffect(() => {
    const trimmed = searchText.trim()
    if (trimmed === (search.search ?? '')) {
      return
    }

    const timer = window.setTimeout(() => {
      lastPushed.current = trimmed
      navigate(loanPaths.accounts, {
        search: {
          search: trimmed === '' ? undefined : trimmed,
          status: search.status,
          branchId: search.branchId,
          customerId: search.customerId,
          groupId: search.groupId,
          page: undefined,
          pageSize: search.pageSize,
        },
        replace: true,
      })
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [
    searchText,
    search.search,
    search.status,
    search.branchId,
    search.customerId,
    search.groupId,
    search.pageSize,
    navigate,
  ])

  const query = useQuery(
    loanAccountsQueryOptions({
      search: search.search,
      status: search.status,
      branchId: search.branchId,
      customerId: search.customerId,
      groupId: search.groupId,
      page,
      pageSize,
    }),
  )

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    updateSearch({ page: next.pageIndex + 1, pageSize: next.pageSize })
  }

  const hasActiveFilters = Boolean(
    search.search ?? search.status ?? search.branchId ?? search.customerId ?? search.groupId,
  )

  const clearFilters = () => {
    lastPushed.current = ''
    setSearchText('')
    navigate(loanPaths.accounts, { search: { pageSize: search.pageSize } })
  }

  return (
    <>
      <PageHeader
        title="Loans"
        description="A row is an account: permanent, one number for life. The figures beside it belong to the round currently open on it."
        actions={
          <>
            {permissions.canViewProducts ? (
              <Button asChild variant="secondary">
                <LoanLink to={loanPaths.products}>
                  <Settings2 />
                  Products
                </LoanLink>
              </Button>
            ) : null}
            {permissions.canView ? (
              <Button asChild variant="secondary">
                <LoanLink to={loanPaths.portfolio}>
                  <Layers />
                  Portfolio
                </LoanLink>
              </Button>
            ) : null}
            {permissions.canOpenAccount ? (
              <Button asChild variant="primary">
                <LoanLink to={loanPaths.newAccount}>
                  <FilePlus2 />
                  Open an account
                </LoanLink>
              </Button>
            ) : null}
          </>
        }
      />

      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-3 rounded-card border border-border-subtle bg-surface p-3">
          <div className="min-w-56 flex-1 space-y-1.5">
            <Label htmlFor="loan-search">Search</Label>
            <Input
              id="loan-search"
              value={searchText}
              autoComplete="off"
              placeholder="Account number or member name"
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>

          <div className="w-44 space-y-1.5">
            <Label htmlFor="loan-status">Account status</Label>
            <Select
              value={search.status ?? ALL}
              onValueChange={(value) =>
                updateSearch({
                  status: value === ALL ? undefined : (value as LoanAccountStatus),
                  page: undefined,
                })
              }
            >
              <SelectTrigger id="loan-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Any status</SelectItem>
                {LOAN_ACCOUNT_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-52 space-y-1.5">
            <Label htmlFor="loan-branch">Branch</Label>
            <Select
              value={search.branchId ?? ALL}
              onValueChange={(value) =>
                updateSearch({ branchId: value === ALL ? undefined : value, page: undefined })
              }
            >
              <SelectTrigger id="loan-branch">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Every branch you can see</SelectItem>
                {(branches.data ?? []).map((branch) => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branchLabel(branch)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {hasActiveFilters ? (
            <Button variant="ghost" onClick={clearFilters}>
              <X />
              Clear
            </Button>
          ) : null}
        </div>

        {search.customerId || search.groupId ? (
          <p className="text-xs text-muted">
            Narrowed to {search.customerId ? 'one member' : 'one group'}
            {search.customerId ? (
              <span className="font-mono"> {search.customerId}</span>
            ) : (
              <span className="font-mono"> {search.groupId}</span>
            )}
            . Clearing the filters shows the whole book again.
          </p>
        ) : null}

        <DataTable
          columns={columns}
          data={query.data?.items}
          rowCount={query.data?.totalCount}
          pagination={{ pageIndex: page - 1, pageSize }}
          onPaginationChange={onPaginationChange}
          sorting={UNSORTED}
          onSortingChange={ignoreSortingChange}
          isLoading={query.isPending}
          isFetching={query.isFetching}
          error={query.isError ? query.error : null}
          onRetry={() => void query.refetch()}
          getRowId={(row) => row.id}
          onRowActivate={(row) => navigate(loanPaths.account(row.id))}
          pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100, 200]}
          emptyTitle={hasActiveFilters ? 'No accounts match' : 'No loan accounts yet'}
          emptyDescription={
            hasActiveFilters
              ? 'Nothing was found with these filters. An account number has to be typed in full to match on its own.'
              : 'Nobody has a loan account at the branches you can see. One is opened against a member and a product, once the savings, deposit and passbook gates are met.'
          }
          emptyAction={
            hasActiveFilters ? (
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                Clear the filters
              </Button>
            ) : permissions.canOpenAccount ? (
              <Button asChild variant="primary" size="sm">
                <LoanLink to={loanPaths.newAccount}>
                  <FilePlus2 />
                  Open an account
                </LoanLink>
              </Button>
            ) : null
          }
        />
      </div>
    </>
  )
}
